import {nanoid} from 'nanoid'
import {FC, useEffect, useState} from 'react'
import {useDropzone} from 'react-dropzone'
import { useIntl } from 'react-intl'
import {
  IAttachment, IAttachmentStatus, IMediaDownloadSignedUrlResponseDto,
  IMediaUploadSignedUrlResponseDto,
} from '../../modules/media/Media.model'
import {
  useMediaCreateDownloadSignedUrlMutation,
  useMediaUploadMutation,
  useMediaCreateUploadSignedUrlMutation,
} from '../../modules/media/Media.api'
import { FormikProps } from 'formik'
import classNames from 'classnames'
import { FilePickedPreview } from './FilePickedPreview'
import { AttachmentListItem } from '../../modules/media/component/AttachmentListItem'
import { DeleteConfirmationModal } from './modal/DeleteConfirmationModal'
import { environment } from '../../util/Environment'
import { Spinner } from './Spinner'
import {get} from 'lodash'

const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

export const sizeFormater = (size) => {
  let l = 0, n = parseInt(size, 10) || 0
  while (n >= 1024 && ++l) {
    n = n / 1024
  }
  return n.toFixed(n < 10 && l > 0 ? 1 : 0) + ' ' + units[l]
}

interface Props {
  loading?: boolean
  disabled?: boolean
  accept?: {}
  uploadedFiles?: IAttachment[]
  formik?: FormikProps<any>
  fieldName?: string
  deleteConfirmationModalId?: string
  fileDeleteOnClickHandler?: (id: string) => void
  setIsAttachmentProcessing?: (isLoading: boolean) => void
}

export const FilePicker: FC<Props> = ({
  loading = false,
  disabled,
  accept = {
    'image/*': ['.png', '.gif', '.jpe', '.jpeg', '.jpg', '.pdf'],
    'text/*': ['.csv', '.txt'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    // 'video/*': ['.mp4', '.mpeg', '.ogg'],
  },
  uploadedFiles,
  formik,
  fieldName,
  deleteConfirmationModalId = 'attachment_delete_confirmation_modal',
  fileDeleteOnClickHandler,
  setIsAttachmentProcessing,
}) => {
  const intl = useIntl()
  const [loadedFiles, setLoadedFiles] = useState<IAttachment[]>([])
  const [mediaCreateUploadSignedUrl] = useMediaCreateUploadSignedUrlMutation()
  const [mediaCreateDownloadSignedUrl] = useMediaCreateDownloadSignedUrlMutation()
  const [mediaUpload] = useMediaUploadMutation()

  const [idSelected, setIdSelected] = useState<string>()
  const [attachment, setAttachment] = useState<IAttachment>()

  useEffect(() => {
    if (uploadedFiles) {
      setLoadedFiles(uploadedFiles)
    }
  }, [uploadedFiles])

  const onDrop = async (acceptedFiles: any[]) => {
    try {
      setIsAttachmentProcessing && setIsAttachmentProcessing(true)
      if (formik && fieldName && acceptedFiles && acceptedFiles.length > 0) {
        const currentFiles = get(formik.values, fieldName)
        const files: IAttachment[] = acceptedFiles.map((file) => {
          return {
            id: `${nanoid()}`,
            file: file,
            fileName: file.name,
            temporalPath: file.path,
            path: '',
            size: file.size,
            contentType: file.type,
            status: IAttachmentStatus.NEW,
            processing: true,
            uploaded: false,
          }
        })

        formik.setFieldValue(fieldName, [...currentFiles, ...files])

        await Promise.all(
          files.map(async (file: IAttachment) => {
            await createUploadPreSignedUrl(file)
            await fileUpload(file)
            formik.setFieldValue(fieldName, [
              ...currentFiles,
              ...files.map((attachment) => {
                if (attachment.id === file.id) {
                  file.processing = false
                  return {
                    ...attachment,
                    file,
                  }
                }
                return attachment
              }),
            ])
          })
        )
      }
    } finally {
      setIsAttachmentProcessing && setIsAttachmentProcessing(false)
    }
  }

  const {getRootProps, getInputProps, open} = useDropzone({
    disabled,
    accept,
    onDrop,
    maxSize: environment.storage.maxSizeAllowed,
  })

  const createUploadPreSignedUrl = async (file: IAttachment) => {
    try {
      const mediaUploadSignedUrlResponse: any = await mediaCreateUploadSignedUrl({
        fileId: file.id,
        contentType: file.contentType,
      })
      if (
        !mediaUploadSignedUrlResponse ||
        !mediaUploadSignedUrlResponse.data ||
        mediaUploadSignedUrlResponse.error
      ) {
        file.error = intl.formatMessage({id: 'ERROR.MESSAGE.UPLOAD'})
        file.status = IAttachmentStatus.ERROR
      } else {
        const mediaUploadSignedUrl: IMediaUploadSignedUrlResponseDto =
          mediaUploadSignedUrlResponse.data
        file.temporalPath = mediaUploadSignedUrl.path
        file.uploadUrl = mediaUploadSignedUrl.url
      }
    } catch (e: any) {
      file.error = intl.formatMessage({id: 'ERROR.MESSAGE.UPLOAD'})
      file.status = IAttachmentStatus.ERROR
    }
  }

  const createDownloadPreSignedUrl = async (file: IAttachment) => {
    try {
      const mediaDownloadSignedUrlResponse: any = await mediaCreateDownloadSignedUrl({
        fileId: file.id,
        path: file.path,
      })
      if (
        !mediaDownloadSignedUrlResponse ||
        !mediaDownloadSignedUrlResponse.data ||
        mediaDownloadSignedUrlResponse.error
      ) {
        file.error = intl.formatMessage({id: 'ERROR.MESSAGE.UPLOAD'})
        file.status = IAttachmentStatus.ERROR
      } else {
        const mediaDownloadSignedUrl: IMediaDownloadSignedUrlResponseDto =
          mediaDownloadSignedUrlResponse.data
        file.downloadUrl = mediaDownloadSignedUrl.url
      }
    } catch (e: any) {
      file.error = intl.formatMessage({id: 'ERROR.MESSAGE.UPLOAD'})
      file.status = IAttachmentStatus.ERROR
    }
  }

  const fileUpload = async (file: IAttachment) => {
    try {
      if (file.uploadUrl) {
        const mediaUploadReponse: any = await mediaUpload({
          signedUrl: file.uploadUrl,
          file: file.file,
          contentType: file.contentType,
        })
        if (!mediaUploadReponse || mediaUploadReponse.error) {
          file.error = intl.formatMessage({id: 'ERROR.MESSAGE.UPLOAD'})
          file.status = IAttachmentStatus.ERROR
        } else {
          file.uploaded = true
        }
      }
    } catch (e: any) {
      file.error = intl.formatMessage({id: 'ERROR.MESSAGE.UPLOAD'})
      file.status = IAttachmentStatus.ERROR
    }
  }

  const fileDownload = async (file: IAttachment) => {
    try {
      if (file.downloadUrl) {
        // window.open(file.downloadUrl, '_blank')?.focus()
        window.open(file.downloadUrl, '_blank')
      }
    } catch (e: any) {
      file.error = intl.formatMessage({id: 'ERROR.MESSAGE.UPLOAD'})
      file.status = IAttachmentStatus.ERROR
    }
  }

  const retryOnClickHandler = async (id: string) => {
    try {
      setIsAttachmentProcessing && setIsAttachmentProcessing(true)
      if (formik && fieldName && id) {
        const files = get(formik.values, fieldName)
        files && formik.setFieldValue(fieldName, [
          ...files.map((pickedFile) => {
            if (pickedFile.id === id) {
              return {
                ...pickedFile,
                error: undefined,
                processing: true,
                uploaded: false,
              }
            }
            return pickedFile
          }),
        ])

        await Promise.all(
          get(formik.values, fieldName)
            .filter((file) => file.id === id)
            .map(async (file) => {
              await createUploadPreSignedUrl(file)
              await fileUpload(file)
              const files = get(formik.values, fieldName)
              files && formik.setFieldValue(
                fieldName,
                files.map((attachment) => {
                  if (attachment.id === file.id) {
                    file.processing = false
                    return {
                      ...attachment,
                      file,
                    }
                  }
                  return attachment
                })
              )
            })
        )
      }
    } finally {
      setIsAttachmentProcessing && setIsAttachmentProcessing(false)
    }
  }

  const downloadOnClickHandler = async (id: string) => {
    try {
      setIsAttachmentProcessing && setIsAttachmentProcessing(true)
      if (formik && fieldName) {
        const files = get(formik.values, fieldName)
        if (files) {
          const file = files.find((file) => file.id === id)
          if (file && !file.file) {
            file.processing = true
            await createDownloadPreSignedUrl(file)
            await fileDownload(file)
            const innerFiles = get(formik.values, fieldName)
            innerFiles && formik.setFieldValue(
              fieldName,
              innerFiles.map((attachment) => {
                if (attachment.id === file.id) {
                  attachment.processing = false
                }
                return attachment
              })
            )
          }
        }
      } else if (loadedFiles) {
        const file = loadedFiles.find((file) => file.id === id)
        if (file && !file.file) {
          file.processing = true
          setLoadedFiles(
            loadedFiles.map((loadedFile) => {
              if (loadedFile.id === id) {
                file.processing = true
                return {
                  ...loadedFile,
                  file,
                }
              }
              return loadedFile
            })
          )
          setAttachment(file)

          await createDownloadPreSignedUrl(file)
          await fileDownload(file)

          file.processing = false
          setLoadedFiles(
            loadedFiles.map((loadedFile) => {
              if (loadedFile.id === id) {
                file.processing = false
                return {
                  ...loadedFile,
                  file,
                }
              }
              return loadedFile
            })
          )
          setAttachment(file)
        }
      }
    } finally {
      setIsAttachmentProcessing && setIsAttachmentProcessing(false)
    }
  }

  const removeOnClickHandler = async (id: string) => {
    try {
      setIsAttachmentProcessing && setIsAttachmentProcessing(true)
      if (id && formik && fieldName) {
        const file = get(formik.values, fieldName).find((file) => file.id === id)
        if (file) {
          if (id === idSelected) {
            setIdSelected(undefined)
            setAttachment(undefined)
          }

          const files = get(formik.values, fieldName)
          if (files) {
            const updatedFiles = files.map((file) => {
              if (file.id === id) {
                return {
                  ...file,
                  processing: true,
                }
              }
              return file
            })

            formik.setFieldValue(
              fieldName,
              updatedFiles
            )
          }

          if ((file.file || file.path) && fileDeleteOnClickHandler) {
            await fileDeleteOnClickHandler(id)
          } else {
            const files = get(formik.values, fieldName)
            files && formik.setFieldValue(
              fieldName,
              files.filter((file) => file.id !== id)
            )
          }
        }
      }
    } finally {
      setIsAttachmentProcessing && setIsAttachmentProcessing(false)
    }
  }

  const rowOnClickHandler = async (id: string) => {
    if (id !== idSelected) {
      setIdSelected(id)
      if (formik && fieldName) {
        const files: IAttachment[] = get(formik.values, fieldName)
        if (files) {
          const found = files.find((file) => file.id === id)
          if (found) {
            setAttachment(found)
          }
        }
      } else if (uploadedFiles) {
        setAttachment(uploadedFiles.find((file) => file.id === id))
      }
    } else {
      setIdSelected(undefined) 
      setAttachment(undefined)
    }
  }

  const closeOnClickHandler = async () => {
    setIdSelected(undefined)
    setAttachment(undefined)
  }

  return (
    <>
      <div className='flex'>
        <div className='row'>
          <div
            className={classNames({
              'd-none': idSelected,
              'd-lg-block': idSelected,
              'col-lg-5': idSelected,
              'col-lg-12': !idSelected,
            })}
          >
            <div className='card-flush'>
              <div className='card-body p-0'>
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    {formik && fieldName && (
                      <>
                        {!disabled && (
                          <div className='position-relative'>
                            <button
                              type='button'
                              className='btn btn-primary btn-sm position-absolute mt-12 start-50 translate-middle'
                              style={{zIndex: 2}}
                              onClick={(event) => {
                                event.stopPropagation()
                                open()
                              }}
                            >
                              {intl.formatMessage({id: 'UTIL.UPLOAD_FILE'})}
                            </button>
                            <div {...getRootProps({className: 'dropzone'})}>
                              <div className='my-2'>
                                <input {...getInputProps()} />
                                <p className='text-center text-muted mt-15'>
                                  {intl.formatMessage({id: 'UTIL.DRAG_AND_DROP'})}
                                  <br />
                                  {intl
                                    .formatMessage({id: 'UTIL.ATTACHMENT.MAX_SIZE'})
                                    .replace(/%s/g, sizeFormater(environment.storage.maxSizeAllowed))}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className='card-scroll pt-5'>
                          {get(formik.values, fieldName) &&
                            get(formik.values, fieldName).length > 0 &&
                            [...get(formik.values, fieldName)]
                              .sort((a, b) => {
                                if (
                                  a.status === IAttachmentStatus.NEW &&
                                  b.status === IAttachmentStatus.NEW
                                ) {
                                  return 0
                                }

                                if (
                                  a.status === IAttachmentStatus.NEW &&
                                  b.status !== IAttachmentStatus.NEW
                                ) {
                                  return -1
                                }

                                if (
                                  a.status !== IAttachmentStatus.NEW &&
                                  b.status === IAttachmentStatus.NEW
                                ) {
                                  return 1
                                }

                                return 0
                              })
                              .map((attachment, index) => (
                                <div key={index}>
                                  {index !== 0 && <div className='separator'></div>}
                                  <AttachmentListItem
                                    key={index}
                                    disabled={formik.isSubmitting}
                                    attachment={attachment}
                                    idSelected={idSelected}
                                    formik={formik}
                                    fieldName={`${fieldName}.${index}`}
                                    rowOnClickHandler={rowOnClickHandler}
                                    deleteOnClickHandler={removeOnClickHandler}
                                    retryOnClickHandler={retryOnClickHandler}
                                    downloadOnClickHandler={downloadOnClickHandler}
                                  ></AttachmentListItem>
                                </div>
                              ))}
                        </div>
                      </>
                    )}

                    {uploadedFiles && !formik && !fieldName && (
                      <div className='card-scroll'>
                        {uploadedFiles.map((attachment, index) => (
                          <div key={index}>
                            {index !== 0 && <div className='separator'></div>}
                            <AttachmentListItem
                              attachment={attachment}
                              idSelected={idSelected}
                              rowOnClickHandler={rowOnClickHandler}
                              downloadOnClickHandler={downloadOnClickHandler}
                            ></AttachmentListItem>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {attachment && (
            <FilePickedPreview
              disabled={formik?.isSubmitting}
              closeOnClickHandler={closeOnClickHandler}
              attachment={attachment}
              formik={formik}
              fieldName={fieldName}
                deleteConfirmationModalId={deleteConfirmationModalId}
              retryOnClickHandler={retryOnClickHandler}
              downloadOnClickHandler={downloadOnClickHandler}
              deleteOnClickHandler={removeOnClickHandler}
            ></FilePickedPreview>
          )}
        </div>
      </div>

      <DeleteConfirmationModal
        id={deleteConfirmationModalId}
        title={intl
          .formatMessage({id: 'UTIL.DELETE_CONFIRMATION.TITLE'})
          .replace(/%s/g, intl.formatMessage({id: 'UTIL.ATTACHMENT'}))}
        description={`${attachment?.fileName.toUpperCase()}`}
        confirmOnClickHandler={() => attachment && removeOnClickHandler(attachment.id)}
      ></DeleteConfirmationModal>
    </>
  )
}
